import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trophy, Gift, Loader2, Phone } from 'lucide-react';
import { useClientAuth } from '@/context/ClientAuthContext';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoyaltyPage = () => {
  const { client, getAuthHeaders } = useClientAuth();
  const [profile, setProfile] = useState(null);
  const [salonPhone, setSalonPhone] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [profileRes, textsRes] = await Promise.all([
        axios.get(`${API}/clients/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/site-texts`).catch(() => ({ data: {} })),
      ]);
      setProfile(profileRes.data);
      setSalonPhone(textsRes.data.salonPhone || '');
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  const points = profile?.loyalty_points || 0;
  const hasReward = points >= 10;

  return (
    <div className="min-h-screen pt-24 pb-12 px-4" data-testid="loyalty-page">
      <div className="max-w-md mx-auto space-y-6">
        <div className="text-center">
          <Trophy className="h-10 w-10 text-accent mx-auto mb-3" />
          <h1 className="text-2xl sm:text-3xl font-bold">Carta Fedeltà</h1>
          <p className="text-muted-foreground mt-1">
            {profile?.first_name} {profile?.last_name}
          </p>
        </div>

        {/* Loyalty Card */}
        <Card className={`card-elegant overflow-hidden ${hasReward ? 'border-accent ring-2 ring-accent/30' : ''}`}>
          <div className="bg-gradient-to-br from-accent/20 via-accent/10 to-transparent p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Punti accumulati</p>
                <p className="text-4xl font-bold text-accent">{points}<span className="text-lg text-muted-foreground font-normal"> / 10</span></p>
              </div>
              <div className="p-3 bg-accent/20 rounded-full">
                {hasReward ? <Gift className="h-8 w-8 text-accent" /> : <Trophy className="h-8 w-8 text-accent" />}
              </div>
            </div>

            {/* Stamp grid */}
            <div className="grid grid-cols-5 gap-2.5" data-testid="stamp-grid">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className={`aspect-square rounded-xl border-2 flex items-center justify-center text-sm font-bold transition-all ${
                    i < points
                      ? 'bg-accent border-accent text-accent-foreground shadow-md shadow-accent/20'
                      : 'border-muted-foreground/20 text-muted-foreground/30 bg-secondary/50'
                  }`}
                  data-testid={`stamp-${i}`}
                >
                  {i < points ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  ) : (
                    i + 1
                  )}
                </div>
              ))}
            </div>
          </div>

          <CardContent className="pt-4 pb-5">
            {hasReward ? (
              <div className="text-center space-y-3">
                <div className="bg-accent/20 p-4 rounded-lg">
                  <Gift className="h-10 w-10 text-accent mx-auto mb-2" />
                  <p className="text-lg font-bold text-accent">TRATTAMENTO IN OMAGGIO!</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Complimenti! Hai raggiunto 10 punti. Presentati in salone per riscattare il tuo trattamento gratuito.
                  </p>
                </div>
                {salonPhone && (
                  <a href={`tel:${salonPhone}`}>
                    <Button variant="outline" className="w-full">
                      <Phone className="h-4 w-4 mr-2" /> Chiama {salonPhone}
                    </Button>
                  </a>
                )}
              </div>
            ) : (
              <div className="text-center space-y-3">
                <p className="text-sm text-muted-foreground">
                  {points === 0
                    ? 'Inizia a raccogliere punti! Ogni taglio vale 1 punto.'
                    : `Ancora ${10 - points} ${10 - points === 1 ? 'punto' : 'punti'} per il trattamento in omaggio!`}
                </p>
                <div className="w-full bg-secondary rounded-full h-2.5">
                  <div
                    className="bg-accent h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${(points / 10) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-elegant">
          <CardContent className="p-5">
            <h3 className="font-semibold mb-2">Come funziona?</h3>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li className="flex items-start gap-2">
                <span className="bg-accent text-accent-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                Ogni taglio ti fa guadagnare 1 punto
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-accent-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                Raggiungi 10 punti per ottenere un trattamento gratuito
              </li>
              <li className="flex items-start gap-2">
                <span className="bg-accent text-accent-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                Dopo il riscatto i punti si azzerano automaticamente
              </li>
            </ul>
          </CardContent>
        </Card>

        <div className="text-center">
          <Link to="/prenota">
            <Button className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="book-from-loyalty">
              Prenota Ora
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyPage;
